import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { Radio, RefreshCw, AlertTriangle, CheckCircle2, User, Coffee } from 'lucide-react';
import { acqDenominator, invalidSeatCount, sumValidSeat } from '../utils/seat-rate.mjs';

const SLOTS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) + '%' : '-');

// 時間別入力ページと同じ指標・色・比率
const METRICS = [
  { key: 'catch',     label: 'キャッチ',   color: 'var(--mark)' },
  { key: 'sticker',   label: 'ティッシュ',     color: 'var(--mark)' },
  { key: 'talk',      label: 'トーク',     color: 'var(--mark)' },
  { key: 'full_talk', label: 'フルトーク', color: 'var(--mark)' },
  { key: 'seat',      label: '着座',       color: 'var(--mark)' },
  { key: 'acq',       label: '獲得',       color: 'var(--gain)' },
];
const RATIOS = [
  { label: 'ティッシュ率',     num: 'sticker',   den: 'catch' },
  { label: 'トーク率',     num: 'talk',      den: 'sticker' },
  { label: 'フルトーク率', num: 'full_talk', den: 'talk' },
  { label: '着座率',       num: 'seat',      den: 'full_talk' },
  { label: '獲得率',       num: 'acq',       den: 'valid_seat' }, // 分母は有効着座（ADR 0028）
  { label: 'キャッチ→獲得', num: 'acq',      den: 'catch' },
];

const INTERVALS = [
  { sec: 15, label: '15秒' },
  { sec: 30, label: '30秒' },
  { sec: 60, label: '1分' },
];

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', boxShadow: 'none', borderRadius: 18, padding: 16, marginBottom: 14 };
const inp = { padding: '10px 14px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, boxSizing: 'border-box', outline: 'none', colorScheme: 'dark' };

function fmtClock(d) {
  try { return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return ''; }
}
// 'YYYY-MM-DD HH:MM:SS' → 'HH:MM'
function lastInputHm(s) {
  if (!s) return null;
  const m = String(s).match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
}

// 読み取り専用セル（時間別入力ページの入力セルと同じ見た目・値のみ表示）
function ReadCell({ label, color, value }) {
  return (
    <div style={{ background: 'var(--paper-sunk)', borderRadius: 12, padding: '8px 6px', textAlign: 'center', border: '1px solid var(--rule-strong)', minWidth: 0 }}>
      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 4, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: value ? color : 'var(--ink-soft)' }}>{value || 0}</div>
    </div>
  );
}

export default function HourlyMonitor() {
  const [today, setToday] = useState(todayJst());
  const [date, setDate] = useState(today);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [intervalSec, setIntervalSec] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const loadRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.getHourlyAll(date);
      setData(res);
      setError('');
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message || '取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [date]);

  loadRef.current = load;
  useEffect(() => { load(); }, [load]);

  // 自動更新（ポーリング）
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => loadRef.current?.(true), intervalSec * 1000);
    return () => clearInterval(id);
  }, [autoRefresh, intervalSec]);

  // 日付またぎ対策：開いたまま/復帰で日付が変わったら「今日」を見ていたら新しい今日へ追従。
  const todayRef = useRef(today);
  useEffect(() => {
    const check = () => {
      const t = todayJst();
      if (t === todayRef.current) return;
      const prev = todayRef.current;
      todayRef.current = t;
      setToday(t);
      setDate((d) => (d === prev ? t : d));
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, []);

  const members = data?.members || [];

  // 選択の既定＝先頭。選択者が居なくなったら先頭に戻す。
  useEffect(() => {
    if (!members.length) return;
    setSelectedId((prev) => (prev && members.some((m) => m.user_id === prev) ? prev : members[0].user_id));
  }, [members]);

  const isToday = date === today;
  const nowHour = data?.now?.hour ?? -1;
  const selected = members.find((m) => m.user_id === selectedId) || null;

  // 稼働ウィンドウ＝入力または休憩マークがある最初〜最後のスロット。
  const activeWindow = (grid) => {
    const active = SLOTS.filter((h) => grid?.[h]?.filled || grid?.[h]?.off);
    return active.length ? [Math.min(...active), Math.max(...active)] : [null, null];
  };
  // 未入力＝当日・過去/現在・稼働ウィンドウ内・データ無し・休憩でない（＝中抜けの入れ忘れ）。
  // 出勤前(遅い出勤)・退勤後(早い退勤)＝ウィンドウ外は対象にしない。
  const isMissing = (grid, h) => {
    if (!isToday || h > nowHour) return false;
    const cell = grid?.[h];
    if (cell?.filled || cell?.off) return false;
    const [first, last] = activeWindow(grid);
    return first != null && h >= first && h <= last;
  };
  const missingCount = (m) => (isToday ? SLOTS.filter((h) => isMissing(m.grid, h)).length : 0);
  // その日1件でも入力/休憩マークがあるか（＝稼働の痕跡）。完全未入力の人を「入力OK」と誤表示しないため。
  const hasAnyActivity = (m) => SLOTS.some((h) => m.grid?.[h]?.filled || m.grid?.[h]?.off);

  // 獲得率の分母（valid_seat）はサーバが返す。古い応答でも率が消えないよう、無ければ着座に戻す（ADR 0028）
  const total = selected?.total ? { ...selected.total, valid_seat: acqDenominator(selected.total) } : {};

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>メンバーを選んで時間別入力をリアルタイム確認（管理者専用）</div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Radio size={20} /> 時間別モニター
        </h1>
      </div>

      {/* コントロール */}
      <div style={{ ...card, padding: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} style={{ ...inp, minHeight: 44 }} />
        <button onClick={() => setDate(new Date(Date.now() - 86400000 + 9 * 3600000).toISOString().slice(0, 10))} style={{ ...inp, cursor: 'pointer', fontWeight: 700, color: 'var(--ink-soft)', minHeight: 44 }}>昨日</button>
        <button onClick={() => setDate(today)} style={{ ...inp, cursor: 'pointer', fontWeight: 700, minHeight: 44, border: `1.5px solid ${isToday ? '#33406E' : 'var(--rule-strong)'}`, background: isToday ? 'var(--mark-soft)' : 'var(--paper-sunk)', color: isToday ? 'var(--mark)' : 'var(--ink-soft)' }}>今日</button>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            style={{ ...inp, cursor: 'pointer', minHeight: 44, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700,
              border: `1.5px solid ${autoRefresh ? '#1E5540' : 'var(--rule-strong)'}`,
              background: autoRefresh ? 'var(--gain-bg)' : 'var(--paper-sunk)',
              color: autoRefresh ? 'var(--gain)' : 'var(--ink-soft)' }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: autoRefresh ? 'var(--gain-bg)' : 'var(--paper-sunk)', boxShadow: autoRefresh ? '0 0 8px transparent' : 'none' }} />
            自動更新{autoRefresh ? 'ON' : 'OFF'}
          </button>
          <select value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))} disabled={!autoRefresh} style={{ ...inp, minHeight: 44, cursor: 'pointer', opacity: autoRefresh ? 1 : 0.5 }}>
            {INTERVALS.map((i) => <option key={i.sec} value={i.sec}>{i.label}ごと</option>)}
          </select>
          <button onClick={() => load(false)} style={{ ...inp, cursor: 'pointer', minHeight: 44, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--mark)' }}>
            <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> 更新
          </button>
        </div>
        <div style={{ width: '100%', fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>
          {lastUpdated ? `最終更新 ${fmtClock(lastUpdated)}` : '—'}
          {data?.now?.time && <span style={{ marginLeft: 10 }}>現在 {data.now.time}（JST）</span>}
          <span style={{ marginLeft: 10 }}>対象 {members.length}名</span>
        </div>
      </div>

      {error && (
        <div style={{ ...card, padding: '12px 14px', background: 'var(--loss-bg)', border: '1px solid #6B3A36', color: 'var(--loss)', fontSize: 'var(--t-note)', fontWeight: 700 }}>
          {error}
        </div>
      )}

      {/* 本日入力なしのメンバーを、プルダウンを開かなくても一目で分かるサマリー（当日のみ） */}
      {isToday && (() => {
        const none = members.filter((m) => !hasAnyActivity(m));
        if (none.length === 0) return null;
        return (
          <div style={{ ...card, padding: '10px 14px', background: 'var(--hold-bg)', border: '1px solid #5C4718' }}>
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 700, lineHeight: 1.5 }}>
               本日まだ入力がありません（{none.length}名）：{none.map((m) => m.name).join('・')}
            </span>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 4 }}>※ 非番の可能性もあります</div>
          </div>
        );
      })()}

      {/* メンバー選択（プルダウン） */}
      <div style={{ ...card, padding: 12 }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--ink-mute)', margin: '0 2px 8px'}}> メンバーを選択</div>
        {members.length === 0 && !loading
          ? <div style={{ color: 'var(--ink-mute)', fontSize: 'var(--t-note)', padding: 6 }}>対象メンバーがいません</div>
          : (
            <select
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              style={{ ...inp, width: '100%', minHeight: 46, cursor: 'pointer', fontSize: 15, fontWeight: 700 }}
            >
              {members.map((m) => {
                const miss = missingCount(m);
                const suffix = !isToday ? ''
                  : !hasAnyActivity(m) ? '（本日入力なし）'
                  : miss > 0 ? `（未入力${miss}枠）`
                  : '（入力OK）';
                return <option key={m.user_id} value={m.user_id}>{m.name}{suffix}</option>;
              })}
            </select>
          )}
      </div>

      {loading && !data && <div style={{ textAlign: 'center', color: 'var(--ink-mute)', fontSize: 'var(--t-note)', padding: 12 }}>読み込み中...</div>}

      {selected && (
        <>
          {/* 選択メンバーの合計＋KPI比率（時間別入力の個人合計と同じ見た目）。
              sticky の top は <main> のパディングボックス基準。<main> に固定ヘッダーぶんの
              padding-top が既にあるので、ここで 60px を足すと二重になり真ん中に貼り付く。 */}
          <div style={{ ...card, position: 'sticky', top: 8, zIndex: 50 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
              <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <User size={15} /> {selected.name} の合計（{isToday ? '本日' : date}）
              </div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>
                {lastInputHm(selected.last_input) ? `最終入力 ${lastInputHm(selected.last_input)}` : '未入力'}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6, marginBottom: 12 }}>
              {METRICS.map((m) => (
                <div key={m.key} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 2, whiteSpace: 'nowrap' }}>{m.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>{total[m.key] || 0}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {RATIOS.map((r) => (
                <div key={r.label} style={{ background: 'var(--paper-sunk)', borderRadius: 8, padding: '6px 4px', textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', whiteSpace: 'nowrap' }}>{r.label}</div>
                  <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--ink)' }}>{pct(total[r.num] || 0, total[r.den] || 0)}</div>
                  {r.den === 'valid_seat' && invalidSeatCount(total) > 0 && (
                <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }} title={`「有効じゃない」${invalidSeatCount(total)}件を分母から除いています`}>有効着座 {acqDenominator(total)}</div>
              )}
                </div>
              ))}
            </div>
          </div>

          {/* 時間帯ごと（読み取り専用） */}
          {SLOTS.map((h) => {
            const cell = selected.grid?.[h] || {};
            const off = !!cell.off;
            const miss = isMissing(selected.grid, h);
            return (
              <div key={h} style={{ ...card, padding: 12,
                borderLeft: off ? '3px solid var(--rule-strong)' : miss ? '3px solid #5C4718' : '3px solid transparent',
                background: off ? 'var(--paper-sunk)' : miss ? 'var(--hold-bg)' : card.background }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: off ? 'var(--ink-soft)' : 'var(--ink)' }}>{h}:00〜{h + 1}:00</div>
                  {off
                    ? <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', gap: 4 }}><Coffee size={12} /> 休憩・非稼働</div>
                    : miss
                      ? <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--hold)', display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={12} /> 未入力</div>
                      : cell.filled && <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--gain)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={12} /> 入力済</div>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, opacity: off ? 0.4 : 1 }}>
                  {METRICS.map((m) => (
                    <ReadCell key={m.key} label={m.label} color={m.color} value={cell[m.key] || 0} />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.6, padding: '4px 6px' }}>
        ※ 読み取り専用の確認画面です。<span style={{ color: 'var(--hold)' }}>オレンジ</span>＝稼働中なのに未入力の枠（当日のみ）。<span style={{ color: 'var(--ink-soft)' }}>グレー</span>＝本人が「休憩」を付けた枠で対象外。出勤前・退勤後（最初〜最後の入力の外）も自動で対象外です。
        {autoRefresh ? `${intervalSec}秒ごとに自動更新中。` : '自動更新はオフです。'}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
